import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Colors } from "../constants/colors";
import {
  LayoutDashboard,
  Building2,
  UserCheck,
  Users,
  Calendar,
  FileText,
  Settings,
  User,
  LogOut,
  Menu,
  X,
  Bell,
  Search,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${Colors.background};
`;

const Sidebar = styled.aside<{ $isOpen: boolean }>`
  width: ${(props) => (props.$isOpen ? "280px" : "80px")};
  background: ${Colors.primary};
  color: ${Colors.white};
  transition: width 0.3s ease;
  position: fixed;
  height: 100vh;
  z-index: 1000;
  overflow-y: auto;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: ${(props) => (props.$isOpen ? "280px" : "0")};
    transform: translateX(${(props) => (props.$isOpen ? "0" : "-100%")});
  }
`;

const SidebarHeader = styled.div`
  padding: 1.5rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${Colors.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: ${Colors.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const NavList = styled.nav`
  padding: 1rem 0;
`;

const NavItem = styled(Link)<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  color: ${(props) =>
    props.$isActive ? Colors.white : "rgba(255, 255, 255, 0.8)"};
  text-decoration: none;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.$isActive ? "rgba(255, 255, 255, 0.1)" : "transparent"};
  margin: 0 0.5rem;
  border-radius: 6px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: ${Colors.white};
  }

  svg {
    flex-shrink: 0;
  }
`;

const NavText = styled.span<{ $isOpen: boolean }>`
  white-space: nowrap;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  transition: opacity 0.2s ease;
  overflow: hidden;
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 80px;
  transition: margin 0.3s ease;
  min-height: 100vh;
  background-color: ${Colors.backgroundSecondary};

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const Header = styled.header`
  background: ${Colors.white};
  padding: 1rem 2rem;
  border-bottom: 1px solid ${Colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SearchContainer = styled.div`
  position: relative;
  min-width: 300px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 1rem 0.5rem 2.5rem;
  border: 1px solid ${Colors.border};
  border-radius: 6px;
  font-size: 0.875rem;
  background: ${Colors.background};
  color: ${Colors.textPrimary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${Colors.primary};
    box-shadow: 0 0 0 2px ${Colors.primary}20;
  }

  &::placeholder {
    color: ${Colors.textMuted};
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${Colors.textMuted};
`;

const NotificationButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  color: ${Colors.textSecondary};
  position: relative;

  &:hover {
    background-color: ${Colors.hoverBackground};
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  width: 8px;
  height: 8px;
  background-color: ${Colors.error};
  border-radius: 50%;
`;

const UserMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${Colors.background};
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  width: 200px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-top: 8px;
  overflow: hidden;
  display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
  z-index: 1000;
`;

const DropdownItem = styled.div`
  padding: 12px 16px;
  color: ${Colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${Colors.background};
    color: ${Colors.primary};
  }
`;

const LogoutButton = styled(DropdownItem)`
  color: ${Colors.error};

  &:hover {
    color: ${Colors.error};
    background-color: rgba(239, 68, 68, 0.1);
  }
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${Colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Colors.white};
  font-weight: 600;
  font-size: 0.875rem;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const UserName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${Colors.textPrimary};
`;

const UserRole = styled.span`
  font-size: 0.75rem;
  color: ${Colors.textMuted};
`;

const ContentWrapper = styled.div`
  padding: 2rem;
  min-height: calc(100vh - 73px);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const navItems = [
  { icon: <LayoutDashboard size={20} />, label: "Dashboard", path: "/" },
  { icon: <Building2 size={20} />, label: "Hospitals", path: "/hospitals" },
  { icon: <Users size={20} />, label: "Users", path: "/users" },
  {
    icon: <Calendar size={20} />,
    label: "Bed Requests",
    path: "/bed-requests",
  },
  // { icon: <Settings size={20} />, label: 'Settings', path: '/settings' },
];

const Layout: React.FC = () => {
  const { user } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  if (user?.role === "hospital_admin") {
    if (navItems[0].label === "Dashboard") {
      navItems.splice(0, 2);
      navItems.unshift({
        icon: <Building2 size={20} />,
        label: "Hospital",
        path: `/hospitals/${user?.hospital}`,
      });
    }
  }
  console.log("navItems", navItems);

  useEffect(() => {
    // Redirect hospital_admin from / to their hospital page
    if (user?.role === "hospital_admin" && location.pathname === "/") {
      navigate(`/hospitals/${user.hospital}`);
    }
  }, [user, location.pathname, navigate]);

  const handleLogout = () => {
    // Clear all items from localStorage
    localStorage.clear();
    // Clear sessionStorage if used
    sessionStorage.clear();
    // Redirect to login page
    navigate("/login");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <LayoutContainer>
      <Sidebar $isOpen={isSidebarOpen}>
        <SidebarHeader>
          <Logo>{isSidebarOpen ? "Hospital Beds Admin" : "HBA"}</Logo>
          <ToggleButton onClick={toggleSidebar}>
            {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </ToggleButton>
        </SidebarHeader>
        <NavList>
          {navItems.map((item) => (
            <NavItem
              key={item.path}
              to={item.path}
              $isActive={location.pathname === item.path}
            >
              {item.icon}
              <NavText $isOpen={isSidebarOpen}>{item.label}</NavText>
            </NavItem>
          ))}
        </NavList>
      </Sidebar>
      <MainContent>
        <Header>
          <ToggleButton onClick={toggleSidebar}>
            <Menu size={20} />
          </ToggleButton>
          <HeaderActions>
            <SearchContainer>
              <SearchIcon size={18} />
              <SearchInput type="text" placeholder="Search..." />
            </SearchContainer>
            <NotificationButton>
              <Bell size={20} />
              <NotificationBadge />
            </NotificationButton>
            <UserMenu
              ref={dropdownRef}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <UserAvatar>AD</UserAvatar>
              <UserInfo>
                <UserName>Admin User</UserName>
                <UserRole>Administrator</UserRole>
              </UserInfo>
              <DropdownMenu $isOpen={isDropdownOpen}>
                <DropdownItem>
                  <User size={16} />
                  Profile
                </DropdownItem>
                <DropdownItem>
                  <Settings size={16} />
                  Settings
                </DropdownItem>
                <LogoutButton onClick={handleLogout}>
                  <LogOut size={16} />
                  Logout
                </LogoutButton>
              </DropdownMenu>
            </UserMenu>
          </HeaderActions>
        </Header>
        <ContentWrapper>
          <Outlet />
        </ContentWrapper>
      </MainContent>
    </LayoutContainer>
  );
};

export default Layout;
